// Slice compose: invio, bozze, stato compose, labels. Logica identica a gmailStore.ts.
import type { MailAdapter } from '../mailAdapter';
import type { MailActions, MailSet, MailGet } from '../storeTypes';
import type { MailLabel, DraftSummary } from '../types';

type ComposeSlice = Pick<
  MailActions,
  'sendEmail' | 'openCompose' | 'setComposing' | 'setComposeMinimized' | 'setReplyingTo' | 'saveDraft'
  | 'deleteDraft' | 'resetCompose' | 'openDraftEditor' | 'loadDrafts' | 'loadLabels'
>;

export function createComposeSlice(set: MailSet, get: MailGet, adapter: MailAdapter): ComposeSlice {
  const LOG = `[mailStore:${adapter.provider}]`;
  // Lock in-flight per saveDraft: serializza i salvataggi concorrenti così che il
  // secondo attenda il primo (che valorizza currentDraftId) prima di leggerlo. Senza
  // questo, due auto-save partiti prima che il primo create abbia risposto leggerebbero
  // entrambi currentDraftId=null → due create → bozza DUPLICATA (aggravato su Outlook
  // dai round-trip createReply + PATCH + upload allegati). Provider-neutro: benigno per
  // Gmail (il secondo save fa un update sullo stesso draft_id invece di un altro create).
  let saveInFlight: Promise<string | null> | null = null;
  return {
    sendEmail: async (params) => {
      try {
        // Attendi un eventuale auto-save in volo: currentDraftId deve essere definitivo
        // prima dell'invio (altrimenti l'invio creerebbe un messaggio nuovo e l'auto-save
        // in coda lascerebbe una bozza orfana). Stesso lock di saveDraft.
        while (saveInFlight) {
          try { await saveInFlight; } catch { /* l'errore del save non blocca l'invio */ }
        }

        // Outlook: se esiste una bozza corrente, l'invio la consuma (PATCH + send della
        // bozza stessa → gli allegati già presenti restano parte dell'email). Gmail
        // ignora draftId (invio raw MIME + delete bozza a valle, flusso invariato).
        const draftId = get().currentDraftId;
        await adapter.send({ ...params, draftId });

        // Bozza consumata dall'invio: azzera l'id PRIMA che il chiamante esegua
        // deleteDraft (che diventa così un no-op). Con ImmutableId l'id della bozza
        // resta lo stesso sul messaggio inviato: una DELETE lo cancellerebbe!
        if (adapter.consumesDraftOnSend && draftId) {
          set({ currentDraftId: null });
        }

        // Reload threads to show the sent message
        await get().loadThreads(params.organizationId, params.userId);

        // If we were viewing a thread, reload it
        if (get().selectedThreadId) {
          await get().selectThread(get().selectedThreadId!, params.organizationId, params.userId);
        }

        return true;
      } catch (error: any) {
        console.error(`${LOG} Send error:`, error);
        return false;
      }
    },

    openCompose: (prefill) => set({
      composing: true,
      composeMinimized: false,
      composePrefill: prefill ?? null,
    }),
    setComposing: (composing) => set({ composing }),
    setComposeMinimized: (composeMinimized) => set({ composeMinimized }),
    setReplyingTo: (messageId, threadId, mode = 'reply') =>
      set({ replyingToMessageId: messageId, replyingToThreadId: threadId, replyMode: mode }),

    saveDraft: async (params) => {
      // Niente da salvare se tutti i campi vuoti
      const hasContent = (params.to || params.subject || params.bodyHtml)?.toString().replace(/<[^>]*>/g, '').trim();
      if (!hasContent) return null;

      // Attendi un eventuale save già in volo: garantisce che currentDraftId sia
      // valorizzato prima che questo save lo legga (no doppio create → no duplicati).
      while (saveInFlight) {
        try { await saveInFlight; } catch { /* l'errore del save precedente non ci riguarda */ }
      }

      const run = (async (): Promise<string | null> => {
        set({ savingDraft: true });
        try {
          const draftId = get().currentDraftId;
          const newDraftId = await adapter.saveDraft({ ...params, draftId });
          if (newDraftId) set({ currentDraftId: newDraftId });
          return newDraftId;
        } catch (err: any) {
          console.error(`${LOG} saveDraft error:`, err);
          return null;
        } finally {
          set({ savingDraft: false });
        }
      })();
      saveInFlight = run;
      try {
        return await run;
      } finally {
        if (saveInFlight === run) saveInFlight = null;
      }
    },

    deleteDraft: async (organizationId, userId) => {
      const draftId = get().currentDraftId;
      if (!draftId) return;
      try {
        await adapter.deleteDraft(organizationId, userId, draftId);
      } catch (err) {
        console.error(`${LOG} deleteDraft error:`, err);
      }
      set({ currentDraftId: null });
    },

    resetCompose: () => set({
      composing: false,
      composeMinimized: false,
      currentDraftId: null,
      savingDraft: false,
      replyingToMessageId: null,
      replyingToThreadId: null,
      editingDraft: null,
      composePrefill: null,
    }),

    openDraftEditor: (draft) => set({
      editingDraft: draft,
      replyingToThreadId: draft.thread_id,
      replyingToMessageId: null,
      currentDraftId: draft.draft_id,
      composing: true,
      composeMinimized: false,
    }),

    loadDrafts: async (organizationId, userId) => {
      try {
        const drafts = await adapter.listDrafts(organizationId, userId);
        const map: Record<string, DraftSummary> = {};
        for (const d of drafts) {
          if (d.thread_id) map[d.thread_id] = d; // ultima bozza per thread
        }
        set({ drafts: map });
      } catch (error: any) {
        console.error(`${LOG} Load drafts error:`, error);
      }
    },

    loadLabels: async (organizationId, userId) => {
      try {
        const labels = await adapter.loadLabels(organizationId, userId);
        const map: Record<string, MailLabel> = {};
        for (const l of labels) map[l.id] = l;
        set({ labels: map });
      } catch (error: any) {
        console.error(`${LOG} Load labels error:`, error);
      }
    },
  };
}
